import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { FloorInfo } from '@/types/api';
import { X } from 'lucide-react';

interface FloorModalProps {
  restaurantId: number;
  floor: FloorInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function FloorModal({ restaurantId, floor, onClose, onSuccess }: FloorModalProps) {
  const [formData, setFormData] = useState({
    name: floor?.name || '',
    width: floor?.width ?? 800,
    height: floor?.height ?? 600,
    sortOrder: floor?.sortOrder ?? 0,
    hidden: floor?.hidden ?? false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<FloorInfo>) => apiClient.createFloor(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create floor';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<FloorInfo>) =>
      apiClient.updateFloor(restaurantId, floor!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update floor';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Floor name is required';
    }
    if (formData.width <= 0) {
      newErrors.width = 'Width must be greater than 0';
    }
    if (formData.height <= 0) {
      newErrors.height = 'Height must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData: Partial<FloorInfo> = {
      name: formData.name,
      width: formData.width,
      height: formData.height,
      sortOrder: formData.sortOrder,
      hidden: formData.hidden,
    };

    if (floor) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {floor ? 'Edit Floor' : 'Add New Floor'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="floor-name" className="block text-sm font-medium text-gray-700 mb-1">
              Floor Name *
            </label>
            <input
              id="floor-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Ground Floor, Terrace"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="floor-width" className="block text-sm font-medium text-gray-700 mb-1">
                Width *
              </label>
              <input
                id="floor-width"
                type="number"
                value={formData.width}
                onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) || 0 })}
                className={`input w-full ${errors.width ? 'border-red-500' : ''}`}
                min={1}
              />
              {errors.width && <p className="text-red-500 text-xs mt-1">{errors.width}</p>}
            </div>
            <div>
              <label htmlFor="floor-height" className="block text-sm font-medium text-gray-700 mb-1">
                Height *
              </label>
              <input
                id="floor-height"
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) || 0 })}
                className={`input w-full ${errors.height ? 'border-red-500' : ''}`}
                min={1}
              />
              {errors.height && <p className="text-red-500 text-xs mt-1">{errors.height}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="floor-sort-order" className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              id="floor-sort-order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              className="input w-full"
            />
          </div>

          <div className="flex items-center">
            <input
              id="floor-hidden"
              type="checkbox"
              checked={formData.hidden}
              onChange={(e) => setFormData({ ...formData, hidden: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="floor-hidden" className="ml-2 text-sm text-gray-700">
              Hidden
            </label>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : floor
                ? 'Update Floor'
                : 'Create Floor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
